import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Upload, FileText, Truck, Check, X, AlertCircle, Image as ImageIcon, Clipboard, Search } from 'lucide-react';
import { colors } from '../theme';

// Função para calcular data + dias úteis (pula sábado e domingo)
const calcularDiasUteis = (dataInicio, diasUteis) => {
  const data = new Date(dataInicio);
  let diasAdicionados = 0;
  
  while (diasAdicionados < diasUteis) {
    data.setDate(data.getDate() + 1);
    // Pular fins de semana (sábado=6, domingo=0)
    if (data.getDay() !== 0 && data.getDay() !== 6) {
      diasAdicionados++;
    }
  }
  
  return data;
};

// Formatar data para YYYY-MM-DD
const formatarDataISO = (data) => {
  return data.toISOString().split('T')[0];
};

// Componente para importação de CT-e
const CTEImportacao = ({ user }) => {
  const [uploadingXML, setUploadingXML] = useState(false);
  const [cteExtraido, setCteExtraido] = useState(null);
  const [pedidosSugeridos, setPedidosSugeridos] = useState([]);
  const [pedidoSelecionado, setPedidoSelecionado] = useState(null);
  const [buscaPedido, setBuscaPedido] = useState('');
  const [buscandoPedido, setBuscandoPedido] = useState(false);
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [dadosEditaveis, setDadosEditaveis] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [listaCTEs, setListaCTEs] = useState([]); // Lista de CTes para aplicar em lote
  const [tipoFrete, setTipoFrete] = useState(''); // CIF ou FOB - obrigatório

  const handleUploadXML = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.xml')) {
      alert('❌ Apenas arquivos XML são permitidos');
      return;
    }
    
    setUploadingXML(true);
    setCteExtraido(null);
    setPedidosSugeridos([]);
    setPedidoSelecionado(null);
    setTipoFrete(''); // Resetar tipo de frete
    
    try {
      const token = localStorage.getItem('primaza_token');
      const formData = new FormData();
      formData.append('file', file);
      
      // Chamar endpoint que apenas extrai os dados (sem salvar)
      const response = await axios.post(
        `${API}/cte/extrair-xml`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      if (response.data.success) {
        setCteExtraido(response.data.cte);
        setPedidosSugeridos(response.data.pedidos_sugeridos || []);
        
        // Processar previsão de entrega - pode vir como "YYYY-MM-DD" ou "YYYY-MM-DD HH:MM"
        let previsaoData = '';
        const previsaoRaw = response.data.cte.previsao_entrega || '';
        if (previsaoRaw) {
          // Extrair apenas a parte da data (YYYY-MM-DD)
          previsaoData = previsaoRaw.split(' ')[0];
        }
        
        // Inicializar dados editáveis
        setDadosEditaveis({
          numero_cte: response.data.cte.numero_cte,
          valor_frete: response.data.cte.valor_frete,
          previsao_entrega: previsaoData,
          codigo_rastreio: response.data.cte.numero_cte // Usa número do CT-e como rastreio
        });
        
        // Se encontrou pedido sugerido, selecionar automaticamente
        if (response.data.pedidos_sugeridos?.length === 1) {
          const pedidoSugerido = response.data.pedidos_sugeridos[0];
          setPedidoSelecionado(pedidoSugerido);
          // Se o pedido já tem tipo de frete definido, usar ele
          if (pedidoSugerido.tipo_frete) {
            setTipoFrete(pedidoSugerido.tipo_frete);
          }
        }
      } else {
        alert('⚠️ ' + (response.data.message || 'Erro ao processar XML'));
      }
    } catch (error) {
      console.error('Erro ao processar XML:', error);
      alert('❌ Erro: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingXML(false);
      e.target.value = '';
    }
  };

  const buscarPedido = async () => {
    if (!buscaPedido.trim()) return;
    
    setBuscandoPedido(true);
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.get(
        `${API}/pedidos?busca=${buscaPedido}&limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResultadosBusca(response.data.pedidos || []);
    } catch (error) {
      console.error('Erro ao buscar pedido:', error);
    } finally {
      setBuscandoPedido(false);
    }
  };

  const adicionarALista = () => {
    if (!cteExtraido || !pedidoSelecionado) {
      alert('⚠️ Selecione um pedido para vincular');
      return;
    }
    
    // Validar tipo de frete (CIF/FOB) - obrigatório
    if (!tipoFrete) {
      alert('⚠️ Selecione o tipo de frete (CIF ou FOB)');
      return;
    }
    
    // Verificar se o pedido já tem CT-e vinculado
    if (pedidoSelecionado.ja_tem_cte) {
      alert(`❌ Este pedido já possui CT-e vinculado (${pedidoSelecionado.numero_cte_existente}). Não é possível importar outro.`);
      return;
    }
    
    // Verificar duplicado na lista
    const jaNaLista = listaCTEs.some(c => c.numero_cte === dadosEditaveis.numero_cte);
    if (jaNaLista) {
      alert('⚠️ Este CT-e já está na lista');
      return;
    }
    
    // Verificar se o pedido já está na lista com outro CT-e
    const pedidoJaNaLista = listaCTEs.some(c => c.pedido_id === pedidoSelecionado.id);
    if (pedidoJaNaLista) {
      alert('⚠️ Este pedido já está na lista com outro CT-e');
      return;
    }
    
    const cteParaLista = {
      ...cteExtraido,
      ...dadosEditaveis,
      pedido_id: pedidoSelecionado.id,
      pedido_numero: pedidoSelecionado.pedido,
      pedido_cliente: pedidoSelecionado.nome_cliente,
      pedido_nfe: pedidoSelecionado.nfe,
      tipo_frete: tipoFrete,
      confirmado: true
    };
    
    setListaCTEs(prev => [...prev, cteParaLista]);
    
    // Limpar para próximo
    setCteExtraido(null);
    setPedidoSelecionado(null);
    setPedidosSugeridos([]);
    setResultadosBusca([]);
    setBuscaPedido('');
    setDadosEditaveis({});
    setTipoFrete('');
  };

  const removerDaLista = (index) => {
    setListaCTEs(prev => prev.filter((_, i) => i !== index));
  };

  const aplicarVinculacoes = async () => {
    const confirmados = listaCTEs.filter(c => c.confirmado);
    
    if (confirmados.length === 0) {
      alert('⚠️ Nenhum CT-e para aplicar');
      return;
    }
    
    if (!window.confirm(`Confirma aplicar ${confirmados.length} CT-e(s) aos pedidos?`)) {
      return;
    }
    
    setSalvando(true);
    
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.post(
        `${API}/cte/aplicar-vinculacoes`,
        { vinculacoes: confirmados },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`✅ ${response.data.atualizados} pedido(s) atualizado(s) com sucesso!`);
      setListaCTEs([]);
    } catch (error) {
      console.error('Erro ao aplicar vinculações:', error);
      alert('❌ Erro: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSalvando(false);
    }
  };

  const descartarCTE = () => {
    setCteExtraido(null);
    setPedidoSelecionado(null);
    setPedidosSugeridos([]);
    setResultadosBusca([]);
    setBuscaPedido('');
    setDadosEditaveis({});
    setTipoFrete('');
  };

  return (
    <div className="space-y-4">
      {/* Preview de Conferência */}
      {cteExtraido && (
        <Card className="border-4" style={{ borderColor: colors.primaza.primary }}>
          <CardHeader>
            <CardTitle className="text-center" style={{ color: colors.primaza.secondary }}>
              📋 Conferência de CT-e
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Dados do CT-e (lado esquerdo) */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-700 border-b pb-1">📄 Dados Extraídos do XML</h3>
                
                <div>
                  <label className="text-xs text-gray-500">Número do CT-e / Código de Rastreio</label>
                  <Input
                    value={dadosEditaveis.numero_cte || ''}
                    onChange={(e) => setDadosEditaveis(prev => ({ ...prev, numero_cte: e.target.value, codigo_rastreio: e.target.value }))}
                    className="font-mono"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-gray-500">Valor do Frete (R$)</label>
                  <Input
                    value={dadosEditaveis.valor_frete || ''}
                    onChange={(e) => setDadosEditaveis(prev => ({ ...prev, valor_frete: e.target.value }))}
                    placeholder="0,00"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-gray-500">Previsão de Entrega</label>
                  <Input
                    type="date"
                    value={dadosEditaveis.previsao_entrega || ''}
                    onChange={(e) => setDadosEditaveis(prev => ({ ...prev, previsao_entrega: e.target.value }))}
                  />
                </div>
                
                {cteExtraido.transportadora && (
                  <div className="text-sm">
                    <span className="text-gray-500">Transportadora:</span>
                    <span className="ml-2 font-medium">{cteExtraido.transportadora}</span>
                  </div>
                )}
                
                {cteExtraido.chave_cte && (
                  <div className="text-xs text-gray-400 font-mono break-all">
                    Chave: {cteExtraido.chave_cte}
                  </div>
                )}
              </div>
              
              {/* Pedido Vinculado (lado direito) */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-700 border-b pb-1">🔗 Vincular ao Pedido</h3>
                
                {/* Pedidos Sugeridos - filtra o pedido já selecionado para não mostrar duplicado */}
                {(() => {
                  // Filtra pedidos sugeridos removendo o já selecionado
                  const pedidosParaMostrar = pedidosSugeridos.filter(p => 
                    !pedidoSelecionado || p.id !== pedidoSelecionado.id
                  );
                  
                  // Só mostra a lista se tiver pedidos para mostrar
                  if (pedidosParaMostrar.length === 0) return null;
                  
                  return (
                    <div className="space-y-2">
                      <label className="text-xs text-gray-500">
                        {pedidoSelecionado 
                          ? 'Outros pedidos encontrados (NF-e referenciada)' 
                          : 'Pedidos Encontrados (NF-e referenciada)'}
                      </label>
                      {pedidosParaMostrar.map((pedido, idx) => (
                        <div
                          key={pedido.id || idx}
                          onClick={() => {
                            if (!pedido.ja_tem_cte) {
                              setPedidoSelecionado(pedido);
                              if (pedido.tipo_frete) setTipoFrete(pedido.tipo_frete);
                            }
                          }}
                          className={`p-2 border rounded transition-all ${
                            pedido.ja_tem_cte 
                              ? 'border-red-300 bg-red-50 cursor-not-allowed opacity-70'
                              : 'border-gray-200 hover:border-blue-300 cursor-pointer'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="font-medium">#{pedido.pedido}</div>
                            {pedido.ja_tem_cte && (
                              <Badge variant="destructive" className="text-xs">
                                CT-e {pedido.numero_cte_existente} já vinculado
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">{pedido.nome_cliente}</div>
                          <div className="text-xs text-gray-400">NF: {pedido.nfe}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                
                {/* Busca Manual */}
                <div className="space-y-2">
                  <label className="text-xs text-gray-500">Ou buscar pedido manualmente</label>
                  <div className="flex gap-2">
                    <Input
                      value={buscaPedido}
                      onChange={(e) => setBuscaPedido(e.target.value)}
                      placeholder="Nº pedido, NF ou cliente..."
                      onKeyPress={(e) => e.key === 'Enter' && buscarPedido()}
                    />
                    <Button
                      onClick={buscarPedido}
                      disabled={buscandoPedido}
                      size="sm"
                    >
                      {buscandoPedido ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search size={16} />}
                    </Button>
                  </div>
                  
                  {/* Resultados da Busca */}
                  {resultadosBusca.length > 0 && (
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {resultadosBusca.map((pedido, idx) => (
                        <div
                          key={idx}
                          onClick={() => {
                            setPedidoSelecionado(pedido);
                            setResultadosBusca([]);
                          }}
                          className="p-2 border rounded cursor-pointer hover:bg-gray-50 text-sm"
                        >
                          <span className="font-medium">#{pedido.pedido}</span>
                          <span className="text-gray-500 ml-2">{pedido.nome_cliente?.substring(0, 30)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Pedido Selecionado */}
                {pedidoSelecionado && (
                  <>
                    {pedidoSelecionado.ja_tem_cte ? (
                      <Alert className="bg-red-50 border-red-200">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                        <AlertDescription>
                          <div className="font-medium text-red-800">⚠️ Pedido #{pedidoSelecionado.pedido} já possui CT-e</div>
                          <div className="text-sm text-red-700">CT-e existente: {pedidoSelecionado.numero_cte_existente}</div>
                          <div className="text-xs text-red-600 mt-1">Não é possível vincular outro CT-e a este pedido.</div>
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <Alert className="bg-green-50 border-green-200">
                        <Check className="h-4 w-4 text-green-600" />
                        <AlertDescription>
                          <div className="font-medium text-green-800">Pedido #{pedidoSelecionado.pedido}</div>
                          <div className="text-sm text-green-700">{pedidoSelecionado.nome_cliente}</div>
                          <div className="text-xs text-gray-500">NF: {pedidoSelecionado.nfe}</div>
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                )}
                
                {/* Tipo de Frete - CIF/FOB - OBRIGATÓRIO */}
                {pedidoSelecionado && !pedidoSelecionado.ja_tem_cte && (
                  <div className="space-y-2">
                    <label className="text-xs text-gray-500 font-medium">
                      Tipo de Frete <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={tipoFrete === 'CIF' ? 'default' : 'outline'}
                        onClick={() => setTipoFrete('CIF')}
                        className={tipoFrete === 'CIF' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                      >
                        CIF (Remetente paga)
                      </Button>
                      <Button
                        type="button"
                        variant={tipoFrete === 'FOB' ? 'default' : 'outline'}
                        onClick={() => setTipoFrete('FOB')}
                        className={tipoFrete === 'FOB' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                      >
                        FOB (Destinatário paga)
                      </Button>
                    </div>
                    {!tipoFrete && (
                      <p className="text-xs text-red-500">* Selecione o tipo de frete para continuar</p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Botões de Ação */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={descartarCTE}>
                <X size={16} className="mr-1" /> Descartar
              </Button>
              <Button
                onClick={adicionarALista}
                disabled={!pedidoSelecionado || !tipoFrete || pedidoSelecionado?.ja_tem_cte}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Check size={16} className="mr-1" /> Adicionar à Lista
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Área de Upload */}
      {!cteExtraido && (
        <div className="flex gap-2 items-center">
          <div className="relative">
            <input
              type="file"
              accept=".xml"
              onChange={handleUploadXML}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={uploadingXML}
            />
            <Button
              variant="default"
              disabled={uploadingXML}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {uploadingXML ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload size={16} className="mr-2" />
              )}
              Selecionar XML do CT-e
            </Button>
          </div>
          <span className="text-sm text-gray-500">
            Upload individual para conferência antes de salvar
          </span>
        </div>
      )}
      
      {/* Lista de CT-es para Aplicar */}
      {listaCTEs.length > 0 && (
        <Card className="border-2 border-green-200">
          <CardHeader className="py-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>📋 CT-es para Aplicar ({listaCTEs.length})</span>
              <Button
                onClick={aplicarVinculacoes}
                disabled={salvando}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {salvando ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check size={16} className="mr-2" />
                )}
                Aplicar Todos
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {listaCTEs.map((cte, index) => {
                // Formatar data para exibição
                const dataFormatada = cte.previsao_entrega 
                  ? new Date(cte.previsao_entrega + 'T00:00:00').toLocaleDateString('pt-BR')
                  : '';
                
                return (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge variant="outline" className="font-mono">CT-e {cte.numero_cte}</Badge>
                      <span className="font-medium text-green-700">R$ {cte.valor_frete}</span>
                      <Badge className={cte.tipo_frete === 'CIF' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}>
                        {cte.tipo_frete}
                      </Badge>
                      {dataFormatada && (
                        <span className="text-sm text-gray-500">📅 {dataFormatada}</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      → Pedido #{cte.pedido_numero} | NF: {cte.pedido_nfe} | {cte.pedido_cliente?.substring(0, 35)}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removerDaLista(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X size={16} />
                  </Button>
                </div>
              )})}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Dicas */}
      {!cteExtraido && listaCTEs.length === 0 && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertDescription className="text-sm text-amber-800">
            <strong>📄 Como funciona:</strong>
            <ol className="mt-2 space-y-1 ml-4 list-decimal">
              <li>Faça upload do XML do CT-e</li>
              <li>Confira os dados extraídos (valor do frete, previsão de entrega)</li>
              <li>Vincule ao pedido correto (automático ou busca manual)</li>
              <li>Adicione à lista e repita para outros CT-es</li>
              <li>Clique em &quot;Aplicar Todos&quot; para salvar</li>
            </ol>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

const Importacoes = ({ user }) => {
  const [uploadingJPG, setUploadingJPG] = useState(false);
  const [embarquesExtraidos, setEmbarquesExtraidos] = useState([]);
  const [pedidosSistema, setPedidosSistema] = useState([]);
  const [matches, setMatches] = useState([]);
  const [processando, setProcessando] = useState(false);
  const [previewEmbarque, setPreviewEmbarque] = useState(null); // Preview antes de adicionar
  const [imagemPreview, setImagemPreview] = useState(null); // URL da imagem
  const [dadosEditaveis, setDadosEditaveis] = useState({}); // Dados editáveis na conferência
  
  const tiposFrete = [
    { value: '', label: 'Selecione...', color: 'bg-gray-100 text-gray-500' },
    { value: 'CIF', label: 'CIF - Frete pago por nós', color: 'bg-green-100 text-green-800' },
    { value: 'FOB', label: 'FOB - Frete pago pelo cliente', color: 'bg-blue-100 text-blue-800' }
  ];

  const handleUploadJPG = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await processarArquivo(file);
  };

  const processarArquivo = async (file) => {
    // Validar tipo
    if (!file.type.startsWith('image/')) {
      alert('❌ Apenas arquivos de imagem (JPG, PNG) são permitidos');
      return;
    }

    setUploadingJPG(true);

    try {
      // Criar preview da imagem
      const imageUrl = URL.createObjectURL(file);
      setImagemPreview(imageUrl);
      
      const token = localStorage.getItem('primaza_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/importar-embarques-correios`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const novosEmbarques = response.data.embarques || [];
      const novosMatches = response.data.matches || [];
      
      if (novosEmbarques.length > 0 && novosMatches.length > 0) {
        // Mostrar preview para conferência
        setPreviewEmbarque(novosMatches[0]);
        
        // Calcular previsão de entrega baseado no prazo
        const prazoDias = novosMatches[0].prazo_dias || 7;
        const previsaoCalculada = calcularDiasUteis(new Date(), prazoDias);
        
        // Inicializar dados editáveis
        setDadosEditaveis({
          nome_destinatario: novosMatches[0].nome_destinatario,
          codigo_rastreamento: novosMatches[0].codigo_rastreamento,
          valor_frete: novosMatches[0].valor_frete,
          prazo_dias: prazoDias,
          tipo_frete: '', // Vazio - operador deve escolher manualmente
          previsao_entrega: formatarDataISO(previsaoCalculada)
        });
      } else {
        setImagemPreview(null);
        alert('⚠️ Nenhum embarque detectado nesta imagem.');
      }
    } catch (error) {
      console.error('Erro ao processar:', error);
      setImagemPreview(null);
      alert('❌ Erro: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingJPG(false);
    }
  };

  const validarEmbarque = async () => {
    if (previewEmbarque) {
      // Validar campos obrigatórios
      if (!dadosEditaveis.tipo_frete) {
        alert('⚠️ Campo obrigatório!\n\nO Tipo de Frete (CIF/FOB) deve ser selecionado.');
        return;
      }
      
      // Verificar duplicados (lista E banco de dados)
      const codigoLimpo = dadosEditaveis.codigo_rastreamento.replace(/\s/g, '').toUpperCase();
      
      // 1. Verificar na lista acumulada
      const naLista = matches.some(m => m.codigo_rastreamento.replace(/\s/g, '').toUpperCase() === codigoLimpo);
      
      if (naLista) {
        alert(`⚠️ CÓDIGO DUPLICADO NA LISTA!\n\nO código ${dadosEditaveis.codigo_rastreamento} já foi adicionado à lista.\n\nNão é possível adicionar o mesmo código duas vezes.`);
        return;
      }
      
      // 2. Verificar no banco de dados
      try {
        const token = localStorage.getItem('primaza_token');
        const resp = await axios.get(
          `${API}/pedidos?busca=${codigoLimpo}&limit=1`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (resp.data.pedidos && resp.data.pedidos.length > 0) {
          const pedidoExiste = resp.data.pedidos[0];
          const confirmar = window.confirm(
            `⚠️ CÓDIGO JÁ EXISTE NO SISTEMA!\n\n` +
            `Pedido: #${pedidoExiste.pedido}\n` +
            `Cliente: ${pedidoExiste.nome_cliente}\n` +
            `Código: ${pedidoExiste.cod_rastreamento}\n\n` +
            `Deseja continuar mesmo assim? (NÃO RECOMENDADO)`
          );
          
          if (!confirmar) {
            return;
          }
        }
      } catch (e) {
        console.error('Erro ao verificar duplicados:', e);
      }
      
      // Usar dados editados + pedido vinculado manual
      const embarqueFinal = {
        ...previewEmbarque,
        nome_destinatario: dadosEditaveis.nome_destinatario,
        codigo_rastreamento: dadosEditaveis.codigo_rastreamento,
        valor_frete: dadosEditaveis.valor_frete,
        prazo_dias: dadosEditaveis.prazo_dias,
        tipo_frete: dadosEditaveis.tipo_frete,
        previsao_entrega: dadosEditaveis.previsao_entrega,
        pedido_vinculado: dadosEditaveis.pedido_vinculado || previewEmbarque.pedido_vinculado,
        pedido_numero: dadosEditaveis.pedido_numero || previewEmbarque.pedido_numero
      };
      
      // Adicionar à lista
      setMatches(prev => [...prev, embarqueFinal]);
      setEmbarquesExtraidos(prev => [...prev, embarqueFinal]);
      
      // Limpar preview e dados editáveis
      setPreviewEmbarque(null);
      setDadosEditaveis({});
      if (imagemPreview) {
        URL.revokeObjectURL(imagemPreview);
        setImagemPreview(null);
      }
      
      // Focar na área de colar para próximo
      document.querySelector('[tabIndex="0"]')?.focus();
    }
  };

  const descartarEmbarque = () => {
    setPreviewEmbarque(null);
    if (imagemPreview) {
      URL.revokeObjectURL(imagemPreview);
      setImagemPreview(null);
    }
  };

  // Função para colar imagem do clipboard (Ctrl+V)
  const handlePasteImagem = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const blob = item.getAsFile();
        
        if (blob) {
          const extension = item.type.split('/')[1] || 'png';
          const file = new File([blob], `embarque_${Date.now()}.${extension}`, {
            type: item.type
          });
          
          await processarArquivo(file);
        }
        break;
      }
    }
  };

  const aplicarVinculacoes = async () => {
    const confirmados = matches.filter(m => m.confirmado && m.pedido_vinculado && !m.codigo_duplicado);
    const duplicados = matches.filter(m => m.codigo_duplicado);
    
    if (duplicados.length > 0) {
      alert(`⚠️ Atenção: ${duplicados.length} código(s) duplicado(s) detectado(s)!\n\nEstes não serão importados pois já existem no sistema.`);
    }
    
    if (confirmados.length === 0) {
      alert('⚠️ Nenhum embarque confirmado para vincular (códigos duplicados foram removidos)');
      return;
    }

    if (!window.confirm(`Confirma vincular ${confirmados.length} embarques aos pedidos?\n\n${duplicados.length > 0 ? `(${duplicados.length} duplicados serão ignorados)` : ''}`)) {
      return;
    }

    setProcessando(true);

    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.post(
        `${API}/aplicar-vinculacoes-embarques`,
        { vinculacoes: confirmados },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`✅ ${response.data.atualizados} pedidos atualizados com sucesso!`);
      
      // Limpar
      setEmbarquesExtraidos([]);
      setMatches([]);
      setPedidosSistema([]);
    } catch (error) {
      console.error('Erro ao aplicar vinculações:', error);
      alert('❌ Erro: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessando(false);
    }
  };

  const toggleConfirmacao = (index) => {
    setMatches(prev => prev.map((m, i) => 
      i === index ? { ...m, confirmado: !m.confirmado } : m
    ));
  };

  const removerEmbarque = (index) => {
    setMatches(prev => prev.filter((_, i) => i !== index));
    setEmbarquesExtraidos(prev => prev.filter((_, i) => i !== index));
  };

  const limparTodos = () => {
    if (window.confirm('Limpar todos os embarques da lista?')) {
      setMatches([]);
      setEmbarquesExtraidos([]);
    }
  };

  const selecionarPedidoManual = (index, pedidoId) => {
    setMatches(prev => prev.map((m, i) => 
      i === index ? { ...m, pedido_vinculado: pedidoId, confirmado: true } : m
    ));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: colors.primaza.secondary }}>
            📥 Importações de Documentos
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Automatize o processo de lançamento de dados
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Card: Embarques Correios */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-100">
                <Truck size={24} className="text-yellow-600" />
              </div>
              <div>
                <span className="text-xl">📸 Embarques dos Correios</span>
                <p className="text-sm text-gray-500 font-normal mt-1">
                  Upload de comprovante JPG/PNG - Extrai código, frete e prazo automaticamente
                </p>
              </div>
              <Badge className="bg-green-100 text-green-800 border-green-300 ml-auto">
                ✅ Ativo
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Modal de Conferência */}
              {previewEmbarque && (
                <Card className="border-4" style={{ borderColor: colors.primaza.primary }}>
                  <CardHeader>
                    <CardTitle className="text-center" style={{ color: colors.primaza.secondary }}>
                      👁️ Conferir Embarque Detectado
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Esquerda: Imagem */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold mb-3 text-sm text-gray-600">Print Colado:</h4>
                        <img 
                          src={imagemPreview} 
                          alt="Preview" 
                          className="w-full h-auto rounded border-2 border-gray-300"
                          style={{ maxHeight: '400px', objectFit: 'contain' }}
                        />
                      </div>
                      
                      {/* Direita: Dados */}
                      <div className="space-y-4">
                        <h4 className="font-semibold mb-3 text-sm text-gray-600">Dados Detectados:</h4>
                        
                        <div className="space-y-3">
                          <div className="bg-white rounded-lg p-3 border-2 border-gray-200">
                            <p className="text-xs text-gray-500 mb-1">Nome do Destinatário</p>
                            <Input
                              value={dadosEditaveis.nome_destinatario || ''}
                              onChange={(e) => setDadosEditaveis(prev => ({...prev, nome_destinatario: e.target.value}))}
                              className="font-bold"
                            />
                          </div>
                          
                          <div className="bg-white rounded-lg p-3 border-2 border-gray-200">
                            <p className="text-xs text-gray-500 mb-1">Código de Rastreamento</p>
                            <Input
                              value={dadosEditaveis.codigo_rastreamento || ''}
                              onChange={(e) => setDadosEditaveis(prev => ({...prev, codigo_rastreamento: e.target.value.toUpperCase()}))}
                              className="font-mono font-bold"
                              maxLength={13}
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white rounded-lg p-3 border-2 border-green-200">
                              <p className="text-xs text-gray-500 mb-1">Valor Frete (Total)</p>
                              <Input
                                value={dadosEditaveis.valor_frete || ''}
                                onChange={(e) => setDadosEditaveis(prev => ({...prev, valor_frete: e.target.value}))}
                                className="font-bold text-green-600"
                                placeholder="0.00"
                              />
                            </div>
                            <div className="bg-white rounded-lg p-3 border-2 border-gray-200">
                              <p className="text-xs text-gray-500 mb-1">Prazo (dias úteis)</p>
                              <Input
                                type="number"
                                value={dadosEditaveis.prazo_dias || ''}
                                onChange={(e) => {
                                  const novoPrazo = parseInt(e.target.value) || 7;
                                  const novaPrevisao = calcularDiasUteis(new Date(), novoPrazo);
                                  setDadosEditaveis(prev => ({
                                    ...prev, 
                                    prazo_dias: novoPrazo,
                                    previsao_entrega: formatarDataISO(novaPrevisao)
                                  }));
                                }}
                                className="font-bold"
                                min={1}
                                max={30}
                              />
                            </div>
                          </div>
                          
                          {/* Previsão de Entrega - calculada automaticamente */}
                          <div className="bg-white rounded-lg p-3 border-2 border-purple-200">
                            <p className="text-xs text-gray-500 mb-1">📅 Previsão de Entrega (Hoje + {dadosEditaveis.prazo_dias || 7} dias úteis)</p>
                            <Input
                              type="date"
                              value={dadosEditaveis.previsao_entrega || ''}
                              onChange={(e) => setDadosEditaveis(prev => ({...prev, previsao_entrega: e.target.value}))}
                              className="font-bold text-purple-600"
                            />
                            {dadosEditaveis.previsao_entrega && (
                              <p className="text-sm text-purple-600 mt-1 font-semibold">
                                → {new Date(dadosEditaveis.previsao_entrega + 'T00:00:00').toLocaleDateString('pt-BR')}
                              </p>
                            )}
                          </div>
                          
                          <div className="bg-white rounded-lg p-3 border-2 border-blue-200">
                            <p className="text-xs text-gray-500 mb-1">Tipo de Frete</p>
                            <select
                              value={dadosEditaveis.tipo_frete || ''}
                              onChange={(e) => setDadosEditaveis(prev => ({...prev, tipo_frete: e.target.value}))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md font-semibold"
                            >
                              {tiposFrete.map(tipo => (
                                <option key={tipo.value} value={tipo.value}>
                                  {tipo.label}
                                </option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              {dadosEditaveis.tipo_frete === 'CIF' 
                                ? 'Primaza paga o frete (conta no Analytics)' 
                                : dadosEditaveis.tipo_frete === 'FOB'
                                ? 'Cliente paga o frete (não conta no Analytics)'
                                : 'Selecione CIF ou FOB'}
                            </p>
                          </div>
                          
                          {previewEmbarque.pedido_numero && (
                            <div className="bg-green-50 rounded-lg p-3 border-2 border-green-200">
                              <p className="text-xs text-green-600 mb-1">Pedido Vinculado</p>
                              <p className="font-bold text-green-700">#{previewEmbarque.pedido_numero} ({previewEmbarque.confianca}% match)</p>
                            </div>
                          )}
                          
                          {/* Seleção Manual se não encontrou */}
                          {!previewEmbarque.pedido_numero && (
                            <div className="bg-yellow-50 rounded-lg p-3 border-2 border-yellow-200">
                              <p className="text-xs text-yellow-700 mb-2 font-semibold">⚠️ Nenhum pedido encontrado automaticamente</p>
                              <div className="flex gap-2">
                                <Input
                                  placeholder="Digite número do pedido (ex: 40123)"
                                  className="flex-1"
                                  onKeyPress={async (e) => {
                                    if (e.key === 'Enter' && e.target.value) {
                                      try {
                                        const token = localStorage.getItem('primaza_token');
                                        const resp = await axios.get(
                                          `${API}/pedidos?busca=${e.target.value}&limit=5`,
                                          { headers: { Authorization: `Bearer ${token}` } }
                                        );
                                        
                                        if (resp.data.pedidos && resp.data.pedidos.length > 0) {
                                          const p = resp.data.pedidos[0];
                                          setDadosEditaveis(prev => ({
                                            ...prev,
                                            pedido_vinculado: p.id,
                                            pedido_numero: p.pedido
                                          }));
                                          alert(`✅ Vinculado ao pedido #${p.pedido} - ${p.nome_cliente}`);
                                        } else {
                                          alert('❌ Pedido não encontrado');
                                        }
                                      } catch (e) {
                                        alert('❌ Erro ao buscar pedido');
                                      }
                                    }
                                  }}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    const input = document.querySelector('input[placeholder*="Digite número"]');
                                    if (input) input.dispatchEvent(new KeyboardEvent('keypress', { key: 'Enter' }));
                                  }}
                                >
                                  Buscar
                                </Button>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">Digite o número e pressione Enter</p>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-3 pt-4">
                          <Button
                            onClick={validarEmbarque}
                            style={{ backgroundColor: colors.primaza.primary }}
                            className="flex-1 text-white font-bold"
                            size="lg"
                          >
                            <Check className="mr-2 h-5 w-5" />
                            ✅ Validar e Adicionar
                          </Button>
                          <Button
                            onClick={descartarEmbarque}
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            size="lg"
                          >
                            <X className="mr-2 h-5 w-5" />
                            Descartar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Upload */}
              <div className="space-y-4">
                {/* Área de Colar (Ctrl+V) */}
                <div 
                  className="border-2 border-dashed rounded-lg p-8 text-center bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer"
                  style={{ borderColor: colors.primaza.primary }}
                  tabIndex={0}
                  onPaste={handlePasteImagem}
                  onClick={(e) => e.currentTarget.focus()}
                >
                  <Clipboard className="mx-auto mb-4 text-blue-600" size={48} />
                  <h3 className="text-lg font-semibold mb-2">Cole o Print Aqui (Ctrl+V)</h3>
                  <p className="text-sm text-gray-600 mb-2">
                    Tire print de <strong>1 embarque</strong> e cole diretamente aqui
                  </p>
                  <p className="text-xs text-blue-600 font-medium">
                    ⌨️ Cmd+Shift+4 (Mac) ou Win+Shift+S (Windows) → Ctrl+V aqui
                  </p>
                </div>

                {/* Ou separador */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="text-xs text-gray-500">ou</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                {/* Upload arquivo */}
                <div className="border-2 border-dashed rounded-lg p-6 text-center" style={{ borderColor: colors.primaza.primary + '80' }}>
                  <ImageIcon className="mx-auto mb-3 text-gray-400" size={40} />
                  <p className="text-sm text-gray-600 mb-3">
                    Ou selecione arquivo salvo
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleUploadJPG}
                    disabled={uploadingJPG}
                    className="hidden"
                    id="upload-jpg"
                  />
                  <label 
                    htmlFor="upload-jpg"
                    className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-white transition-colors cursor-pointer ${
                      uploadingJPG ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                    }`}
                    style={{ backgroundColor: uploadingJPG ? '#999' : colors.primaza.primary }}
                  >
                    {uploadingJPG ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Selecionar Arquivo
                      </>
                    )}
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    JPG ou PNG | Máx: 10MB
                  </p>
                </div>
              </div>

              {/* Resultados da Extração */}
              {matches.length > 0 && (
                <div className="space-y-4" id="lista-embarques">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg">
                      📋 Embarques Acumulados ({matches.length})
                    </h3>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={limparTodos}
                          disabled={processando}
                        >
                          <X className="mr-2 h-4 w-4" />
                          Limpar Todos
                        </Button>
                        <Button
                          onClick={aplicarVinculacoes}
                          disabled={processando || matches.filter(m => m.confirmado).length === 0}
                          style={{ backgroundColor: colors.primaza.primary }}
                          className="text-white"
                        >
                          {processando ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Aplicando...
                            </>
                          ) : (
                            <>
                              <Check className="mr-2 h-4 w-4" />
                              Aplicar {matches.filter(m => m.confirmado).length} Confirmados
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                  {/* Lista de Matches */}
                  <div className="space-y-3">
                    {matches.map((match, index) => (
                      <Card key={index} className={`${match.confirmado ? 'ring-2 ring-green-500' : ''}`}>
                        <CardContent className="pt-4">
                          <div className="flex items-start gap-4">
                            {/* Checkbox */}
                            <button
                              onClick={() => !match.codigo_duplicado && toggleConfirmacao(index)}
                              disabled={match.codigo_duplicado}
                              className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all mt-1 ${
                                match.codigo_duplicado
                                  ? 'bg-red-100 border-red-300 cursor-not-allowed opacity-50'
                                  : match.confirmado 
                                    ? 'bg-green-500 border-green-500 text-white' 
                                    : 'border-gray-300 hover:border-green-500'
                              }`}
                            >
                              {match.codigo_duplicado ? <X size={14} className="text-red-600" /> : match.confirmado && <Check size={14} />}
                            </button>

                            {/* Dados */}
                            <div className="flex-1">
                              {/* Nome e badge de match */}
                              <div className="flex items-center justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-bold text-lg">{match.nome_destinatario}</h4>
                                  {match.confianca >= 90 ? (
                                    <Badge className="bg-green-100 text-green-800 border-green-300">
                                      {match.confianca}% match
                                    </Badge>
                                  ) : match.confianca >= 70 ? (
                                    <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                                      {match.confianca}% match
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-red-100 text-red-800 border-red-300">
                                      {match.confianca}% match
                                    </Badge>
                                  )}
                                </div>
                                
                                {/* Botão Deletar */}
                                <button
                                  onClick={() => removerEmbarque(index)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1 transition-colors"
                                  title="Remover este embarque"
                                >
                                  <X size={20} />
                                </button>
                              </div>

                              {/* Pedido vinculado ou duplicado */}
                              {match.codigo_duplicado ? (
                                <Alert className="mb-3 bg-red-50 border-red-300">
                                  <AlertCircle className="h-4 w-4 text-red-600" />
                                  <AlertDescription className="text-sm text-red-800">
                                    <strong>⚠️ Código Duplicado!</strong> Este código de rastreamento já existe no sistema. 
                                    Não é possível importar novamente.
                                  </AlertDescription>
                                </Alert>
                              ) : match.pedido_vinculado ? (
                                <div className="bg-blue-50 rounded-lg p-3 mb-3">
                                  <p className="text-sm text-blue-700">
                                    → Pedido <span className="font-bold">#{match.pedido_numero}</span> 
                                    {match.pedidos_alternativos && match.pedidos_alternativos.length > 0 && (
                                      <span className="ml-2 text-xs">
                                        ({match.pedidos_alternativos.length} alternativas)
                                      </span>
                                    )}
                                  </p>
                                </div>
                              ) : (
                                <Alert className="mb-3">
                                  <AlertCircle className="h-4 w-4" />
                                  <AlertDescription className="text-sm">
                                    Nenhum pedido encontrado. Selecione manualmente abaixo.
                                  </AlertDescription>
                                </Alert>
                              )}

                              {/* Dados do embarque */}
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                                <div className="bg-gray-50 rounded p-2">
                                  <p className="text-xs text-gray-500">Código Rastreamento</p>
                                  <p className="font-mono font-semibold text-xs">{match.codigo_rastreamento}</p>
                                </div>
                                <div className="bg-gray-50 rounded p-2">
                                  <p className="text-xs text-gray-500">Valor Frete</p>
                                  <p className="font-semibold text-green-700">R$ {match.valor_frete}</p>
                                </div>
                                <div className="bg-gray-50 rounded p-2">
                                  <p className="text-xs text-gray-500">Prazo → Previsão</p>
                                  <p className="font-semibold text-blue-700">
                                    {match.prazo_dias}d → {match.prev_entrega_calculada ? new Date(match.prev_entrega_calculada).toLocaleDateString('pt-BR') : '-'}
                                  </p>
                                </div>
                              </div>

                              {/* Seleção manual se houver alternativas */}
                              {match.pedidos_alternativos && match.pedidos_alternativos.length > 0 && (
                                <div className="mt-3">
                                  <select
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                    value={match.pedido_vinculado || ''}
                                    onChange={(e) => selecionarPedidoManual(index, e.target.value)}
                                  >
                                    <option value="">Selecionar pedido manualmente...</option>
                                    {match.pedidos_alternativos.map(p => (
                                      <option key={p.id} value={p.id}>
                                        Pedido #{p.pedido} - {p.nome_cliente} ({p.confianca}% match)
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Instruções */}
              {matches.length === 0 && (
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription className="text-sm text-blue-800">
                    <strong>💡 Dica para Melhor Resultado:</strong>
                    <ol className="mt-2 space-y-1 ml-4 list-decimal">
                      <li><strong>Tire print de 1 embarque por vez</strong> (corte a imagem)</li>
                      <li>Faça upload do print individual</li>
                      <li>Sistema detecta com 100% de precisão</li>
                      <li>Repita para cada embarque</li>
                      <li>Mais rápido e confiável! ✨</li>
                    </ol>
                    <p className="mt-2 text-xs">
                      ℹ️ Prints individuais têm taxa de sucesso de 100% vs. 70% com JPG completo
                    </p>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Card: CTe - Conhecimento de Transporte */}
        <Card className="border-2 border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <FileText size={24} className="text-amber-600" />
              </div>
              <div>
                <span className="text-xl">📄 CT-e - Conhecimento de Transporte</span>
                <p className="text-sm text-gray-500 font-normal mt-1">
                  Upload de XML - Extrai valor do frete, previsão de entrega e código de rastreio
                </p>
              </div>
              <Badge className="bg-green-100 text-green-800 border-green-300 ml-auto">
                ✅ Ativo
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CTEImportacao user={user} />
          </CardContent>
        </Card>

        {/* Card: Notas Fiscais (Futuro) */}
        <Card className="opacity-60">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-200">
                <FileText size={24} className="text-gray-500" />
              </div>
              <div>
                <span className="text-xl">📋 Notas Fiscais (XML)</span>
                <p className="text-sm text-gray-500 font-normal mt-1">
                  Upload de XML de NF-e - Extrai valores e chaves automaticamente
                </p>
              </div>
              <Badge className="bg-gray-100 text-gray-600 border-gray-300 ml-auto">
                🔜 Em breve
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">Funcionalidade será implementada em breve</p>
              <p className="text-xs mt-2">Importação automática de dados fiscais</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Importacoes;
